import { useTranslations } from 'next-intl';
import { useEffect } from 'react';
import { BaseLineRight } from '@/app/components/icons/BaseLineRight';
import { StarIcon } from '@/app/components/icons/StarIcon';
import { Button } from '@/app/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/app/components/ui/dialog';
import { Input } from '@/app/components/ui/input';
import { Switch } from '@/app/components/ui/switch';
import { Textarea } from '@/app/components/ui/textarea';
import { toast } from '@/app/components/ui/use-toast';
import { cn } from '@/app/utils';
import { useChannelConfig } from '../../_hook/use-channel-config';
import { GUIDE_LINK } from './constants';
import type { ConfigChannelItem } from './types';

interface ChannelConfigModalProps {
  channel: ConfigChannelItem;
  clawId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export const ChannelConfigModal = ({ channel, clawId, open, onOpenChange, onSuccess }: ChannelConfigModalProps) => {
  const t = useTranslations();
  const { status, values, errorMessage, loadConfig, updateValue, submitConfig, reset } = useChannelConfig({
    clawId,
    channelId: channel.id,
    fields: channel.fields,
    fixedConfig: channel.fixedConfig,
    onSuccess,
  });

  useEffect(() => {
    if (open) {
      loadConfig();
      return;
    }

    reset();
  }, [loadConfig, open, reset]);

  const handleSubmit = async () => {
    const { error } = await submitConfig();

    if (error) {
      toast({ type: 'error', message: error });
      return;
    }

    toast({ type: 'success', message: t('claw.channelConfig.common.saveSuccess') });
    onOpenChange(false);
  };

  const isSaving = status === 'saving';
  const isLoading = status === 'idle' || status === 'loading';
  const ChannelIcon = channel.icon;
  const fieldsWithoutEnabled = channel.fields.filter((field) => field.key !== 'enabled');
  const enabledField = channel.fields.find((field) => field.key === 'enabled');
  const enabledFieldId = enabledField ? `channel-config-${channel.id}-${enabledField.key}` : '';
  const enabledCurrentValue = enabledField ? values[enabledField.key] : undefined;
  const enabledBooleanValue = typeof enabledCurrentValue === 'boolean' ? enabledCurrentValue : false;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        inheritTheme
        closeClassName="top-5 right-5"
        className="w-[488px] max-w-[calc(100vw-32px)] gap-0 border border-system-white12 bg-[#121316] light:bg-system-bg02 p-0 shadow-[0px_30px_200px_0px_rgba(0,0,0,0.48)]"
      >
        <DialogHeader className="border-system-white12 border-b px-6 py-5">
          <div className="flex items-center justify-between gap-4">
            <DialogTitle className="flex items-center gap-2 font-medium text-base text-system-text01">
              <ChannelIcon className="size-5" />
              <span>{t(`claw.channelConfig.${channel.modalTitleKey}`)}</span>
            </DialogTitle>
          </div>
        </DialogHeader>

        {isLoading ? (
          <div className="flex flex-col items-center justify-center px-6 py-16">
            <div className="size-10 animate-spin rounded-full border-2 border-system-white24 border-t-system-white" />
            <p className="mt-4 text-sm text-system-text03">{t('claw.channelConfig.common.loadingConfig')}</p>
          </div>
        ) : status === 'error' ? (
          <div className="flex flex-col items-center justify-center px-6 py-16">
            <p className="text-sm text-system-text01">{t('claw.channelConfig.common.loadConfigFailed')}</p>
            <p className="mt-2 text-center text-system-text03 text-xs">{errorMessage}</p>
            <Button variant="outline" size="sm" className="mt-6 rounded-8 px-6 py-2" onClick={() => void loadConfig()}>
              {t('claw.channelConfig.common.retry')}
            </Button>
          </div>
        ) : (
          <>
            <div className="space-y-5 px-6 py-6">
              <div
                className="flex w-full cursor-pointer items-center justify-between gap-2 rounded-8 border border-[#5765F266] bg-[#5765F23D] px-4 py-3 text-left"
                onClick={() => {
                  window.open(GUIDE_LINK, '_blank');
                }}
              >
                <StarIcon className="size-4" />
                <div className="flex-1 bg-gradient-to-r light:bg-none from-system-white to-[#9BA4FF] bg-clip-text light:text-system-text01 text-sm text-transparent">
                  {t('claw.channelConfig.guideTitle', { channel: t(`claw.channelConfig.${channel.titleKey.menu}`) })}
                </div>
                <BaseLineRight className="size-4 text-system-text01" />
              </div>

              {fieldsWithoutEnabled.map((field) => {
                const fieldId = `channel-config-${channel.id}-${field.key}`;
                const currentValue = values[field.key];
                const currentTextValue = typeof currentValue === 'string' ? currentValue : '';
                const currentBooleanValue = typeof currentValue === 'boolean' ? currentValue : false;
                const shouldUseTextarea = field.inputType === 'textarea';

                return (
                  <div key={field.key} className={cn('space-y-2', field.valueType === 'boolean' && 'pt-1')}>
                    {field.valueType === 'boolean' ? (
                      <div className="flex items-center justify-between gap-4">
                        <div className="min-w-0">
                          <div className="flex items-center gap-1 text-sm text-system-text01">
                            <label htmlFor={fieldId}>{t(`claw.channelConfig.${field.labelKey}`)}</label>
                            {field.required && <span className="text-red-400">*</span>}
                          </div>
                        </div>
                        <Switch
                          id={fieldId}
                          checked={currentBooleanValue}
                          onCheckedChange={(checked) => updateValue(field.key, checked)}
                          className="h-7 w-12 data-[state=checked]:bg-[#5765F2] data-[state=unchecked]:bg-system-white24"
                        />
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-1">
                          <label className="text-sm text-system-text01" htmlFor={fieldId}>
                            {t(`claw.channelConfig.${field.labelKey}`)}
                          </label>
                          {field.required && <span className="text-red-400 text-sm">*</span>}
                        </div>

                        {shouldUseTextarea ? (
                          <Textarea
                            id={fieldId}
                            value={currentTextValue}
                            placeholder={
                              field.placeholderKey ? t(`claw.channelConfig.${field.placeholderKey}`) : undefined
                            }
                            onChange={(event) => updateValue(field.key, event.target.value)}
                            className="h-[100px] min-h-[100px] rounded-8 border-system-white12 bg-system-white08 light:bg-system-bg03 px-4 py-3 light:text-system-text01 text-sm text-system-white light:placeholder:text-system-text05 placeholder:text-system-white24"
                          />
                        ) : (
                          <Input
                            id={fieldId}
                            type="text"
                            value={currentTextValue}
                            placeholder={
                              field.placeholderKey ? t(`claw.channelConfig.${field.placeholderKey}`) : undefined
                            }
                            onChange={(event) => updateValue(field.key, event.target.value)}
                            className="h-12 rounded-8 border-system-white12 bg-system-white08 light:bg-system-bg03 px-4 light:text-system-text01 text-sm text-system-white light:placeholder:text-system-text05 placeholder:text-system-white24"
                          />
                        )}

                        {field.descriptionKey && (
                          <p className="text-system-text04 text-xs">
                            {t(`claw.channelConfig.${field.descriptionKey}`)}
                          </p>
                        )}
                      </>
                    )}
                  </div>
                );
              })}

              <div className="my-6 h-px w-full bg-system-white12" />

              {enabledField && (
                <div key={enabledField.key} className="flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1 text-sm text-system-text01">
                      <label htmlFor={enabledFieldId}>
                        {t('claw.channelConfig.fields.enabled.label', {
                          channel: t(`claw.channelConfig.${channel.titleKey.menu}`),
                        })}
                      </label>
                      {enabledField.required && <span className="text-red-400">*</span>}
                    </div>
                  </div>
                  <Switch
                    id={enabledFieldId}
                    checked={enabledBooleanValue}
                    onCheckedChange={(checked) => updateValue(enabledField.key, checked)}
                    className="h-7 w-12 data-[state=checked]:bg-[#5765F2] data-[state=unchecked]:bg-system-white24"
                  />
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-4 px-6 pb-6">
              <Button
                variant="outline"
                className="h-12 rounded-12 px-8 text-sm"
                onClick={() => onOpenChange(false)}
                disabled={isSaving}
              >
                {t('claw.channelConfig.common.cancel')}
              </Button>
              <Button
                variant="solid"
                className="h-12 rounded-12 px-8 font-semibold text-sm"
                onClick={handleSubmit}
                loading={isSaving}
                disabled={isSaving}
              >
                {t('claw.channelConfig.common.save')}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
